<template>
  <div class="card bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-2 border-orange-200 dark:border-orange-700 shadow-lg" :key="language">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center shadow-md">
        <span class="text-xl">🧮</span>
      </div>
      <h3 class="text-lg font-bold text-orange-900 dark:text-orange-100">
        Trip Calculator
      </h3>
    </div>

    <div class="space-y-4">
      <!-- Route Info -->
      <div v-if="stops.length >= 2" class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-3 border-2 border-blue-200 dark:border-blue-700">
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-base">🏁</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-blue-600 dark:text-blue-300 font-semibold">{{ t('start') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[0].name }}</div>
            </div>
          </div>
          <div class="flex items-center justify-center text-gray-400">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-base">🎯</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-red-600 dark:text-red-300 font-semibold">{{ t('finish') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[stops.length - 1].name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vehicle Selector -->
      <VehicleSelector
        v-if="vehicles.length > 0"
        :vehicles="vehicles"
        :model-value="selectedVehicleId"
        @update:model-value="handleVehicleChange"
        @add-vehicle="$emit('add-vehicle')"
      />

      <!-- Fuel Consumption -->
      <div>
        <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <span>{{ isElectric ? '⚡' : '⛽' }}</span>
          {{ isElectric ? 'Energy Consumption (kWh/100km)' : 'Fuel Consumption (L/100km)' }}
        </label>
        <input
          v-model.number="localFuelConsumption"
          @input="debouncedUpdate"
          type="number"
          step="0.1"
          min="0"
          :max="isElectric ? 40 : 50"
          class="input-field"
          :placeholder="isElectric ? '18.0' : '8.0'"
        />
      </div>

      <!-- Fuel Price -->
      <div>
        <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <span>💰</span>
          {{ isElectric ? 'Electricity Price (€/kWh)' : 'Fuel Price (€/L)' }}
        </label>
        <input
          v-model.number="localFuelPrice"
          @input="debouncedUpdate"
          type="number"
          step="0.01"
          min="0"
          max="10"
          class="input-field"
          :placeholder="isElectric ? '0.45' : '1.50'"
        />
      </div>

      <!-- Calculation Results -->
      <div v-if="calculation" class="bg-gradient-to-br from-white to-orange-50/50 dark:from-gray-800 dark:to-gray-700/50 rounded-lg p-4 border-2 border-orange-200 dark:border-orange-700">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xl">📊</span>
          <h4 class="text-sm font-bold text-orange-900 dark:text-orange-100">
            Trip Summary
          </h4>
        </div>
        
        <div class="grid grid-cols-2 gap-3">
          <div class="text-center bg-white/70 dark:bg-gray-800/70 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
            <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {{ formatDistance(calculation.totalDistance, settings.distanceUnit, 0).split(' ')[0] }}
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              🛣️ {{ settings.distanceUnit === 'km' ? 'Kilometers' : 'Miles' }}
            </div>
          </div>
          
          <div class="text-center bg-white/70 dark:bg-gray-800/70 p-3 rounded-lg border border-purple-200 dark:border-purple-700">
            <div class="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {{ formatDuration(calculation.totalDuration) }}
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              ⏱️ Total Time
            </div>
          </div>
          
          <div class="text-center bg-white/70 dark:bg-gray-800/70 p-3 rounded-lg border border-green-200 dark:border-green-700">
            <div class="text-2xl font-bold text-green-600 dark:text-green-400">
              {{ formatCurrency(calculatedFuelCost, currency) }}
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              💰 {{ energyLabel }} Cost
            </div>
          </div>
          
          <div class="text-center bg-white/70 dark:bg-gray-800/70 p-3 rounded-lg border border-orange-200 dark:border-orange-700">
            <div class="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {{ calculatedFuelConsumption.toFixed(1) }}{{ energyUnit }}
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {{ isElectric ? '⚡' : '⛽' }} {{ energyLabel }} Needed
            </div>
          </div>
        </div>

        <!-- Breakdown -->
        <div class="mt-4 pt-4 border-t-2 border-orange-200 dark:border-orange-700">
          <h5 class="text-sm font-bold text-orange-900 dark:text-orange-100 mb-2 flex items-center gap-2">
            <span>📋</span> Breakdown
          </h5>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Driving time:</span>
              <span>{{ formatDuration(calculation.totalDuration - totalStopDuration) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Stop duration:</span>
              <span>{{ formatDuration(totalStopDuration) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Avg. speed:</span>
              <span>{{ averageSpeed.toFixed(0) }} km/h</span>
            </div>
          </div>
        </div>
      </div>

      <!-- No data state -->
      <div v-else class="text-center py-6">
        <div class="text-4xl mb-2">🧮</div>
        <p class="text-gray-600 dark:text-gray-400 text-sm">
          Add at least 2 stops to see calculations
        </p>
      </div>

      <!-- Cost Breakdown Section -->
      <div v-if="calculation && costSummary && costSummary.totalCost > 0" class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ t('costs') }}
          </h4>
          <button @click="$emit('openCosts')" class="text-primary-600 hover:text-primary-700 text-sm font-medium">
            {{ t('manage') }} →
          </button>
        </div>
        
        <div class="space-y-2">
          <!-- Total Cost -->
          <div class="flex justify-between items-center pb-2 border-b border-blue-200 dark:border-blue-700">
            <span class="font-medium text-gray-900 dark:text-gray-100">{{ t('totalCost') }}:</span>
            <span class="text-lg font-bold text-blue-600">
              {{ formatCurrency(costSummary.totalCost, costSummary.currency) }}
            </span>
          </div>

          <!-- Fuel vs Other -->
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">{{ t('fuelCostLabel') }}:</span>
            <span>{{ formatCurrency(costSummary.fuelCost, costSummary.currency) }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">{{ t('otherCosts') }}:</span>
            <span>{{ formatCurrency(costSummary.otherCosts, costSummary.currency) }}</span>
          </div>

          <!-- Category breakdown (top 3) -->
          <div v-if="topCostCategories.length > 0" class="pt-2 mt-2 border-t border-blue-100 dark:border-blue-800">
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">{{ t('topCategories') }}:</div>
            <div 
              v-for="cat in topCostCategories" 
              :key="cat.category"
              class="flex justify-between text-xs"
            >
              <span>{{ getCategoryLabel(cat.category) }}</span>
              <span class="font-medium">{{ formatCurrency(cat.amount, costSummary.currency) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Cost Button -->
      <button 
        v-if="calculation"
        @click="$emit('addCost')"
        class="w-full px-4 py-2 bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 transition-colors"
      >
        💰 {{ t('addCost') }}
      </button>

      <!-- Premium features teaser -->
      <div v-if="!isPremium" class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
        <div class="flex items-start gap-3">
          <div class="text-purple-600 dark:text-purple-400">
            ⭐
          </div>
          <div>
            <h4 class="text-sm font-medium text-purple-900 dark:text-purple-100">
              {{ t('upgradeToPremium') }}
            </h4>
            <p class="text-xs text-purple-700 dark:text-purple-300 mt-1">
              {{ t('premiumFeatures') }}
            </p>
            <button class="mt-2 text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-full transition-colors">
              {{ t('upgradeNow') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { debounce } from '@/utils/debounce'
import { formatCurrency, convertCurrency } from '@/utils/currency'
import { formatDistance, formatDuration as formatDur } from '@/utils/format'
import type { TripCalculation, CostSummary, CostCategory, Currency, Vehicle } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import VehicleSelector from './VehicleSelector.vue'

interface Props {
  calculation?: TripCalculation | null
  fuelConsumption: number
  fuelPrice: number
  stops: any[]
  isPremium?: boolean
  costSummary?: CostSummary | null
  vehicles?: Vehicle[]
  selectedVehicleId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  calculation: null,
  fuelConsumption: 8.0,
  fuelPrice: 1.5,
  isPremium: false,
  costSummary: null,
  vehicles: () => [],
  selectedVehicleId: null
})

const { t } = useI18n()
const settingsStore = useSettingsStore()
const { language, currency, settings } = storeToRefs(settingsStore)

const emit = defineEmits<{
  updateFuelSettings: [settings: { consumption: number; price: number }]
  addCost: []
  openCosts: []
  'update:vehicleId': [vehicleId: string | null]
  'add-vehicle': []
}>()

const localFuelConsumption = ref(props.fuelConsumption)
const localFuelPrice = ref(props.fuelPrice)

// Get selected vehicle
const selectedVehicle = computed(() => {
  if (!props.selectedVehicleId || !props.vehicles) return null
  return props.vehicles.find(v => v.id === props.selectedVehicleId)
})

// Check if vehicle is electric
const isElectric = computed(() => {
  return selectedVehicle.value?.fuelType === 'electric'
})

// Energy unit (L or kWh)
const energyUnit = computed(() => {
  return isElectric.value ? 'kWh' : 'L'
})

// Energy label (Fuel/Energy)
const energyLabel = computed(() => {
  return isElectric.value ? 'Energy' : 'Fuel'
})

// Handle vehicle change
function handleVehicleChange(vehicleId: string | null) {
  emit('update:vehicleId', vehicleId)
}

// Watch for prop changes
watch(() => props.fuelConsumption, (value) => {
  localFuelConsumption.value = value
})

watch(() => props.fuelPrice, (value) => {
  localFuelPrice.value = value
})

const calculatedFuelCost = computed(() => {
  if (!props.calculation) return 0
  const distanceKm = props.calculation.totalDistance / 1000 // Convert meters to km
  const costInEUR = (distanceKm / 100) * localFuelConsumption.value * localFuelPrice.value
  
  // Convert from EUR to selected currency
  return convertCurrency(costInEUR, 'EUR', currency.value)
})

const calculatedFuelConsumption = computed(() => {
  if (!props.calculation) return 0
  const distanceKm = props.calculation.totalDistance / 1000 // Convert meters to km
  return (distanceKm / 100) * localFuelConsumption.value
})

const totalStopDuration = computed(() => {
  return props.stops.reduce((sum, stop) => sum + (stop.duration || 0), 0)
})

const averageSpeed = computed(() => {
  if (!props.calculation) return 0
  const drivingTime = props.calculation.totalDuration - totalStopDuration.value
  const distanceKm = props.calculation.totalDistance / 1000 // Convert meters to km
  return drivingTime > 0 ? (distanceKm / (drivingTime / 60)) : 0
})

const debouncedUpdate = debounce(() => {
  emit('updateFuelSettings', {
    consumption: localFuelConsumption.value,
    price: localFuelPrice.value
  })
}, 500)

// Top cost categories (exclude fuel, show top 3)
const topCostCategories = computed(() => {
  if (!props.costSummary) return []
  
  return Object.entries(props.costSummary.byCategory)
    .filter(([category, amount]) => category !== 'fuel' && amount > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([category, amount]) => ({ category: category as CostCategory, amount }))
})

function getCategoryLabel(category: CostCategory): string {
  const key = `cost${category.charAt(0).toUpperCase()}${category.slice(1)}` as any
  return t(key) || category
}

function formatDuration(minutes: number): string {
  return formatDur(minutes, true)
}
</script>